// ===== Global WebRTC Call Manager =====
// Handles incoming call notifications throughout the entire app

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { 
  Phone, 
  VideoCall, 
  CallEnd
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { socket } from '../Socket';

const GlobalCallManager = ({ currentUser, onCallAccept, onCallReject }) => {
  const [incomingCall, setIncomingCall] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);

  // Audio for incoming call ringtone
  const ringtoneRef = useRef(null);

  useEffect(() => {
    if (!currentUser) {
      console.warn('🌍 No currentUser provided to GlobalCallManager');
      return;
    }

    // Use existing socket connection
    socketRef.current = socket;
    setIsConnected(socket.connected);

    console.log('🌍 GlobalCallManager initializing with user:', currentUser);
    console.log('🌍 Socket connected:', socket.connected);
    console.log('🌍 Socket ID:', socket.id);

    // Setup global WebRTC call listeners
    const setupGlobalCallListeners = () => {
      if (!socketRef.current) {
        console.error('🌍 No socket available in GlobalCallManager');
        return;
      }

      if (!currentUser) {
        console.error('🌍 No currentUser available in GlobalCallManager');
        return;
      }

      console.log('🌍 Setting up global WebRTC call listeners for user:', currentUser.socketId);

      // Wait for socket to connect if not already connected
      if (!socketRef.current.connected) {
        console.log('🌍 Socket not connected, waiting...');
        socketRef.current.on('connect', () => {
          console.log('🌍 Socket connected, setting up listeners');
          setupListeners();
        });
      } else {
        setupListeners();
      }

      function setupListeners() {
        console.log('🌍 Setting up global call listeners');

        // Join user-specific connection based on role
        if (currentUser.role === 'doctor') {
          console.log('👨‍⚕️ Global: Joining as doctor:', currentUser.socketId);
          socketRef.current.emit('join_doctor', { userId: currentUser.socketId });
        } else {
          console.log('👤 Global: Joining as patient:', currentUser.socketId);
          socketRef.current.emit('join_patient', { userId: currentUser.socketId });
        }

        // Connect user directly
        console.log('🌍 Emitting user-connect for:', currentUser.socketId);
        socketRef.current.emit('user-connect', {
          userId: currentUser.socketId
        });

        // Listen for incoming WebRTC calls globally
        console.log('🌍 Setting up WebRTC event listeners');
        socketRef.current.on('webrtc-call-offer', handleGlobalIncomingCall);
        socketRef.current.on('webrtc-call-ended', handleGlobalCallEnded);
        socketRef.current.on('webrtc-call-rejected', handleGlobalCallRejected);

        // Add test listener to see if any events are coming through
        socketRef.current.onAny((eventName, ...args) => {
          console.log('🌍 GlobalCallManager received event:', eventName, args);
        });
      }

      // Monitor connection status
      socketRef.current.on('connect', () => {
        console.log('✅ Global call manager connected');
        setIsConnected(true);
      });

      socketRef.current.on('disconnect', () => {
        console.log('❌ Global call manager disconnected');
        setIsConnected(false);
      });
    };

    setupGlobalCallListeners();

    // Cleanup function
    return () => {
      if (socketRef.current) {
        socketRef.current.off('webrtc-call-offer', handleGlobalIncomingCall);
        socketRef.current.off('webrtc-call-ended', handleGlobalCallEnded);
        socketRef.current.off('webrtc-call-rejected', handleGlobalCallRejected);
      }
      stopRingtone();
    };
  }, [currentUser]);

  const handleGlobalIncomingCall = useCallback((data) => {
    console.log('🌍📞 Global incoming WebRTC call:', data);

    // If the chat window is open it handles the call locally – don't show a duplicate notification
    if (window.__chatWindowOpen) {
      console.log('🌍📞 Chat window is open, deferring incoming call to chat component');
      return;
    }

    // The backend already routes this event to the correct socket via io.to(socketId),
    // so if we received it the call is for us. We still validate receiverId when present
    // as an extra safety check, but we never block if it's missing (older payloads or
    // backends that don't forward it).
    const isForMe = !data.receiverId ||
      data.receiverId === currentUser?.socketId ||
      data.receiverId === currentUser?._id;

    if (isForMe) {
      console.log('🌍📞 Incoming call is for this user, showing notification');
      setIncomingCall({
        callerId: data.callerId,
        callType: data.callType,
        offer: data.offer,
        sessionId: data.sessionId,
        callerName: data.callerName || 'Unknown',
        appointmentId: data.appointmentId
      });

      // Start ringtone
      playRingtone();
    } else {
      console.log('🌍📞 Call receiverId does not match current user, ignoring', {
        dataReceiverId: data.receiverId,
        currentUserSocketId: currentUser?.socketId,
        currentUserId: currentUser?._id
      });
    }
  }, [currentUser]);

  const handleGlobalCallEnded = useCallback(() => {
    console.log('🌍📞 Global call ended');
    setIncomingCall(null);
    stopRingtone();
  }, []);

  const handleGlobalCallRejected = useCallback(() => {
    console.log('🌍📞 Global call rejected');
    setIncomingCall(null);
    stopRingtone();
  }, []);

  const playRingtone = useCallback(() => {
    try {
      // Create audio element for ringtone
      if (!ringtoneRef.current) {
        ringtoneRef.current = new Audio('/ringtone.mp3');
        ringtoneRef.current.loop = true;
        ringtoneRef.current.volume = 0.7;
      }
      ringtoneRef.current.play().catch(e => {
        console.log('Ringtone play failed (user interaction required):', e);
      });
    } catch (error) {
      console.log('Ringtone error:', error);
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  }, []);

  const acceptCall = useCallback(() => {
    console.log('✅ Global call accepted');
    stopRingtone();
    
    if (onCallAccept && incomingCall) {
      onCallAccept(incomingCall);
    }
    
    setIncomingCall(null);
  }, [incomingCall, onCallAccept]);

  const rejectCall = useCallback(() => {
    console.log('❌ Global call rejected');
    stopRingtone();
    
    if (incomingCall && socketRef.current) {
      socketRef.current.emit('webrtc-call-rejected', {
        receiverId: incomingCall.callerId,
        sessionId: incomingCall.sessionId,
        reason: 'Call declined by user'
      });
    }

    if (onCallReject && incomingCall) {
      onCallReject(incomingCall);
    }
    
    setIncomingCall(null);
  }, [incomingCall, onCallReject]);

  // Don't render if no current user or no incoming call
  if (!currentUser || !incomingCall) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        width: '300px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        border: '1px solid #e0e0e0',
        zIndex: 10000,
        overflow: 'hidden',
        animation: 'slideInRight 0.3s ease-out'
      }}
    >
      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
          }
        `}
      </style>
      
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#128C7E',
        color: 'white',
        fontSize: '12px',
        fontWeight: '500'
      }}>
        📞 Incoming Call
      </div>
      
      {/* Content */}
      <div style={{
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: '#25D366',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '16px',
          fontWeight: 'bold',
          color: 'white',
          animation: 'pulse 2s infinite',
          flexShrink: 0
        }}>
          {incomingCall.callerName.charAt(0).toUpperCase()}
        </div>
        
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '500',
            color: '#333',
            marginBottom: '2px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {incomingCall.callerName}
          </div>
          <div style={{
            fontSize: '12px',
            color: '#666'
          }}>
            {incomingCall.callType === 'video' ? 'Video Call' : 'Voice Call'}
          </div>
        </div>
      </div>
      
      {/* Actions */}
      <div style={{
        display: 'flex',
        borderTop: '1px solid #e0e0e0'
      }}>
        <button
          onClick={rejectCall}
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: '#fff',
            border: 'none',
            color: '#ff4444',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            borderRight: '1px solid #e0e0e0'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#fff'}
        >
          <CallEnd style={{ fontSize: '14px' }} />
          Decline
        </button>
        
        <button
          onClick={acceptCall}
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: '#fff',
            border: 'none',
            color: '#25D366',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#fff'}
        >
          {incomingCall.callType === 'video' ? 
            <VideoCall style={{ fontSize: '14px' }} /> :
            <Phone style={{ fontSize: '14px' }} />
          }
          Accept
        </button>
      </div>
    </div>
  );
};

export default GlobalCallManager;
